<template>
  <div class="container">
    <h1>Añadir Nueva Reservación</h1>
    <form @submit.prevent="makeReservation">
      <div>
        <label for="name">Nombre:</label>
        <input type="text" v-model="state.reservation.name" id="name" required>
      </div>
      <div>
        <label for="email">Email:</label>
        <input type="email" v-model="state.reservation.email" id="email" required>
      </div>
      <div>
        <label for="phone">Teléfono:</label>
        <input type="tel" v-model="state.reservation.phone" id="phone" required>
      </div>
      <div>
        <label for="date">Fecha:</label>
        <input type="date" v-model="state.reservation.date" id="date" required @change="checkRules">
      </div>
      <div>
        <label for="time">Hora:</label>
        <select v-model="state.reservation.time" id="time" required>
          <option v-for="time in availableTimes" :key="time" :value="time">{{ formatTime(time) }}</option>
        </select>
        <p v-if="timeError">{{ timeError }}</p>
      </div>
      <div>
        <label for="people">Número de personas:</label>
        <input type="number" v-model="state.reservation.people" id="people" required min="1" :max="maxPeople">
      </div>
      <div>
        <label for="specialNotes">Notas Especiales:</label>
        <textarea v-model="state.reservation.specialNotes" id="specialNotes" :disabled="noSpecialNotes" placeholder="Escriba sus notas aquí..."></textarea>
      </div>
      <div>
        <input type="checkbox" v-model="noSpecialNotes" id="noSpecialNotes">
        <label for="noSpecialNotes">No tengo notas especiales</label>
      </div>
      <p v-if="specialNotesError">{{ specialNotesError }}</p>
      <button type="submit" :disabled="v$.reservation.$invalid || timeError || loading">
        <template v-if="loading">
          <span class="spinner"></span> Procesando...
        </template>
        <template v-else>
          Reservar
        </template>
      </button>
    </form>
  </div>
</template>

<script>
import { ref, watch } from 'vue';
import useVuelidate from '@vuelidate/core';
import { required, email, minLength } from '@vuelidate/validators';
import { collection, addDoc, getDocs, query, where } from 'firebase/firestore';
import { db } from '../firebase/init';
import { sendReservationReceivedNotification } from '../utils/reservation';

export default {
  name: 'AdminReservationView',
  setup() {
    const state = ref({
      reservation: {
        name: '',
        email: '',
        phone: '',
        date: '',
        time: '',
        people: '1',
        specialNotes: ''
      }
    });

    const maxPeople = ref(50);
    const timeError = ref('');
    const specialNotesError = ref('');
    const noSpecialNotes = ref(false);
    const loading = ref(false);
    const availableTimes = ref([]);
    const OPENING_HOUR = 11;
    const CLOSING_HOUR = 21;
    const SLOT_INTERVAL_MINUTES = 30;
    const MAX_PEOPLE_PER_SLOT = 100;
    const allowedDays = [3, 4, 5, 6, 0]; // miércoles a domingo

    const generateTimeSlots = () => {
      const slots = [];
      let minutes = OPENING_HOUR * 60;
      const closingMinutes = CLOSING_HOUR * 60;

      while (minutes <= closingMinutes) {
        const hour = Math.floor(minutes / 60);
        const minute = minutes % 60;
        const hourStr = String(hour).padStart(2, '0');
        const minuteStr = String(minute).padStart(2, '0');
        slots.push(`${hourStr}:${minuteStr}`);
        minutes += SLOT_INTERVAL_MINUTES;
      }

      return slots;
    };

    const baseTimes = generateTimeSlots();

    const rules = {
      reservation: {
        name: { required },
        email: { required, email },
        phone: { required, minLength: minLength(10) },
        date: { required },
        time: { required },
        people: { required },
        specialNotes: { required: () => !noSpecialNotes.value || state.value.reservation.specialNotes.trim() !== '' }
      }
    };

    const v$ = useVuelidate(rules, state);

     const checkRules = () => {
  if (!state.value.reservation.date) {
    timeError.value = 'Por favor, selecciona una fecha.';
    availableTimes.value = [];
    return;
  }

  const selectedDate = new Date(state.value.reservation.date);
  const day = selectedDate.getDay();

  if (!allowedDays.includes(day)) {
    availableTimes.value = [];
    timeError.value = 'Las reservaciones solo están disponibles de miércoles a domingo.';
    return;
  }

  timeError.value = '';
  availableTimes.value = [...baseTimes];
  filterAvailableTimes();
};

    const filterAvailableTimes = async () => {
      const reservedTimes = await getReservedTimes(state.value.reservation.date);

      // Asegurarse de que todas las horas estén disponibles mientras no hayan alcanzado las 100 personas
      availableTimes.value = availableTimes.value.filter(time => {
        const totalPeopleForTime = reservedTimes[time] || 0;
        return totalPeopleForTime < MAX_PEOPLE_PER_SLOT; // Permitir las horas con menos de 100 personas
      });
    };

    const getReservedTimes = async (date) => {
      const q = query(collection(db, 'reservations'), where('date', '==', date));
      const querySnapshot = await getDocs(q);

      const reservedTimes = {};

      // Contabilizar el número de personas por hora
      querySnapshot.forEach(doc => {
        const reservation = doc.data();
        if (!reservedTimes[reservation.time]) {
          reservedTimes[reservation.time] = 0;
        }
        reservedTimes[reservation.time] += parseInt(reservation.people, 10) || 0; // Sumar la cantidad de personas por hora
      });

      return reservedTimes;
    };


    const makeReservation = async () => {
      v$.value.$touch();
      if (v$.value.$invalid || timeError.value) {
        console.error('Validation failed');
        specialNotesError.value = 'Debe escribir notas especiales o seleccionar la opción "No tengo notas especiales".';
        return;
      }
      loading.value = true;
      try {
        // Obtener el número de personas reservadas en el horario seleccionado
        const reservedTimes = await getReservedTimes(state.value.reservation.date);
        const totalPeopleForTime = reservedTimes[state.value.reservation.time] || 0;

        // Si el total de personas supera el límite de 100, mostrar un mensaje y no permitir la reservación
        if (totalPeopleForTime + parseInt(state.value.reservation.people) > MAX_PEOPLE_PER_SLOT) {
          alert('El límite de 100 personas por hora ha sido alcanzado. Por favor, comuníquese con el restaurante para más detalles.');
          loading.value = false;
          return;
        }

        // Si no se ha alcanzado el límite, proceder a crear la reservación
        const docRef = await addDoc(collection(db, 'reservations'), { ...state.value.reservation, status: 'pending' });
        console.log('Reservation added with ID: ', docRef.id);
        state.value.reservation.id = docRef.id;
        await sendReservationReceivedNotification(state.value.reservation);
        alert(`Reservación creada con éxito, ID: ${docRef.id}`);
        state.value.reservation = {
          name: '',
          email: '',
          phone: '',
          date: '',
          time: '',
          people: '1',
          specialNotes: '' // Restablecer notas especiales
        };
        noSpecialNotes.value = false;
        v$.value.$reset();
      } catch (error) {
        console.error('Error al crear la reservación:', error);
      } finally {
        loading.value = false;
      }
    };


    const formatTime = (time) => {
      const [hour, minute] = time.split(':');
      const date = new Date();
      date.setHours(hour, minute);
      const options = { hour: '2-digit', minute: '2-digit', hour12: true, timeZone: 'America/Puerto_Rico' };
      return new Intl.DateTimeFormat('en-US', options).format(date);
    };

    watch(() => state.value.reservation.date, checkRules);

    return {
      v$,
      state,
      maxPeople,
      makeReservation,
      checkRules,
      timeError,
      specialNotesError,
      noSpecialNotes,
      loading,
      availableTimes,
      formatTime
    };
  }
};
</script>

<style src="./AdminReservationView.css" scoped></style>
